/**
 * Przekrój pracownika przez moduły — WARTOŚCI RUNTIME
 * ===================================================
 *
 * Typy tego kontraktu są w `@insolitum/types` (`worker-insights.ts`); tutaj
 * mieszka to, co żyje w czasie wykonania: katalog metryk, reguła dostępu do
 * modułu, formatowanie i składanie długich wierszy z RPC w karty wyników.
 *
 * DLACZEGO ROZBICIE NA DWA PAKIETY. `packages/types/src/index.ts` re-eksportuje
 * z sufiksem `.js` (konwencja NodeNext), a `webpack.resolve.extensionAlias`
 * ustawia tylko `apps/module-lexshift/next.config.js`. Pierwszy import
 * WARTOŚCI z `@insolitum/types` wywrócił kiedyś build LexShiftu i jest to
 * odnotowane w nagłówku tamtej konfiguracji. Barrel `packages/utils/src/index.ts`
 * re-eksportuje BEZ rozszerzeń, więc webpack rozwiązuje go bez sztuczek — i to
 * jest jedyny powód, dla którego wartości idą tutaj, a nie obok typów.
 *
 * Funkcje są czyste: bez Reacta, bez klienta Supabase, bez `import.meta.env`
 * i bez `process.env`. Powłoka czyta `VITE_*`, moduły czytają `NEXT_PUBLIC_*`
 * — wspólny kod nie może dotknąć żadnego z nich.
 */

import type {
  WorkerMetric,
  WorkerMetricFormat,
  WorkerMetricTone,
  WorkerModuleScorecard,
  WorkerModuleState,
  WorkerModuleStatRow,
} from '@insolitum/types';

// ─── Organy, do których przypisuje się pracownika ───────────────────────────
//
// Źródłem prawdy jest `MODULE_REGISTRY` w powłoce
// (`apps/universe-shell/src/modules/registry.ts`), ale rejestr nie przechodzi
// przez granicę build-ów: powłoka to Vite, moduły to Next, a `registry.ts`
// czyta `import.meta.env`, którego webpack Next.js nie rozumie. Do niedawna
// każda zakładka Pracownicy nosiła więc WŁASNĄ kopię tej listy — trzy kopie
// przy trzech soczewkach, sześć przy sześciu — i żaden test o tym nie mówił.
//
// Lista jest niezbędna, a nie ozdobna: reguła dostępu brzmi „brak deklaracji
// ⇒ WSZYSTKIE organy", więc bez pełnego składu nie da się odpowiedzieć nawet
// na pytanie „czy ten człowiek należy do tego modułu". Kopia zgubiona o jeden
// slug cicho wypisuje ludzi z organu.
//
// Zgodności z rejestrem pilnuje `apps/universe-shell/src/modules/__tests__/registry.test.ts`
// (porównanie zbioru i kolejności). Dokładając moduł do rejestru, dołóż go tutaj
// — test powie, jeśli tego nie zrobisz.
export const WORKER_MODULE_SLUGS: readonly string[] = [
  'waterandice',
  'neurosense',
  'lexshift',
  'gamification',
  'agenda',
  'provisio',
  'fabrica',
  'conspectus',
  'artifex',
  'furnus',
];

// ─── Dostęp pracownika do modułu ────────────────────────────────────────────
//
// Przypisanie trzyma `shop_floor_workers.preferences.module_access` (tablica
// slugów). Reguła „brak klucza ⇒ dostęp do wszystkiego" jest CELOWO permisywna
// i musi taka zostać: na produkcji 12 z 24 kart nie ma tego klucza w ogóle, bo
// zapisywał go dotąd JEDEN ekran (edycja pracownika w powłoce), a nie czytał
// go NIKT. Gdyby domyślną odpowiedzią było „brak dostępu", dzień wdrożenia tego
// filtra opróżniłby zakładkę Pracownicy w każdym module naraz.
//
// Dlatego: filtrujemy w WIDOKU, nigdy w zapytaniu, a permisywny default
// zostaje. Admin najpierw widzi luki (kartoteka pokazuje je wprost), dopiero
// potem je uzupełnia.
//
// ZASTRZEŻENIE POJEDYNCZEGO ORGANU (`requireExplicit`). Permisywny default
// odpowiada na pytanie „czy wolno mu tu wejść", a nie „czy on tu należy".
// Fabrica potrzebuje tego drugiego: hala ma widzieć SWOJĄ załogę, a nie całą
// firmę, bo brygadzista wybiera z tej listy człowieka do etapu. Dlatego
// soczewka przyjmuje `requireExplicit` — wtedy przypisany jest WYŁĄCZNIE ten,
// kto ma slug wpisany wprost. Włącza to pojedyncza zakładka, nigdy helper: dla
// pozostałych organów default pozostaje permisywny i nic się nie zmienia.
//
// Ludzie bez deklaracji nie znikają — wpadają do `candidates` (szuflada
// „Przypisz") i są policzeni w `undeclared`, więc zakładka może nazwać lukę
// wprost zamiast po cichu skracać listę. To jest ta sama zasada „pokaż brak,
// nie udawaj, że go nie ma", której pilnuje reszta tego pliku.

/** Kształt `preferences` w części, która nas obchodzi. */
export interface WorkerAccessPreferences {
  module_access?: unknown;
  [key: string]: unknown;
}

/** Minimum, jakiego potrzebują helpery — pasuje i do `ShopFloorWorker`, i do wierszy z lżejszych zapytań. */
export interface WorkerAccessLike {
  preferences?: WorkerAccessPreferences | null;
}

/**
 * Efektywna lista modułów pracownika — TRZY STANY (decyzja właściciela Q3 = A,
 * migracja 857):
 *
 *   - brak klucza albo nie-tablica → nie zadeklarowano → WSZYSTKIE `allSlugs`
 *     (permisywny default, patrz komentarz wyżej);
 *   - tablica, także `[]` → dokładnie wpisane niepuste napisy; `[]` (albo same
 *     śmieci w tablicy) znaczy „żaden organ”, a NIE „wszystkie”.
 *
 * Do 857 pusta tablica wracała do pełnej listy, więc odebranie ostatniego organu
 * rozszerzało dostęp do wszystkich. Tę samą regułę liczy w bazie
 * `public.moduly_osoby`; zgodność obu pilnuje wspólna tabela przypadków
 * `tests/sql/f0-trzy-stany-organow/przypadki.json`.
 */
export function workerModuleAccess(
  preferences: WorkerAccessPreferences | null | undefined,
  allSlugs: readonly string[]
): string[] {
  const raw = preferences?.module_access;
  if (!Array.isArray(raw)) return [...allSlugs];
  return Array.from(new Set(raw.filter((s): s is string => typeof s === 'string' && s.length > 0)));
}

/** Czy pracownik jest przypisany do modułu. */
export function hasModuleAccess(
  preferences: WorkerAccessPreferences | null | undefined,
  slug: string,
  allSlugs: readonly string[]
): boolean {
  return workerModuleAccess(preferences, allSlugs).includes(slug);
}

/**
 * Czy pracownik ma JAKĄKOLWIEK deklarację modułów — czyli czy o jego przynależności
 * ktoś kiedykolwiek zdecydował.
 *
 * Odróżnienie „nie należy" od „nikt nie ustawił" jest tu całym sensem: pierwsze
 * to decyzja, drugie to luka w danych. Bez tego rozróżnienia zaostrzony filtr
 * wygląda jak zniknięcie ludzi, a nie jak brakująca konfiguracja.
 */
export function hasModuleDeclaration(
  preferences: WorkerAccessPreferences | null | undefined
): boolean {
  // Jawne `[]` jest decyzją („żaden organ”), nie luką — trzy stany, migracja 857.
  return Array.isArray(preferences?.module_access);
}

/**
 * Czy slug jest wpisany WPROST — bez permisywnego defaultu.
 *
 * `hasModuleAccess` odpowiada „czy wolno mu tu wejść" (brak deklaracji ⇒ tak),
 * ta funkcja odpowiada „czy ktoś go tu zapisał" (brak deklaracji ⇒ nie).
 */
export function hasExplicitModuleAccess(
  preferences: WorkerAccessPreferences | null | undefined,
  slug: string
): boolean {
  const raw = preferences?.module_access;
  if (!Array.isArray(raw)) return false;
  return raw.includes(slug);
}

/**
 * Nowa lista modułów po przełączeniu jednego. Zwraca SAMĄ LISTĘ, nie cały
 * obiekt `preferences` — zapis idzie przez RPC `universe_worker_module_access_set`,
 * które robi `jsonb_set` po stronie bazy. Klient nigdy nie nadpisuje całego
 * bloba, bo wtedy dwie równoległe edycje kasują sobie nawzajem klucze, których
 * żadna z nich nie dotykała.
 */
export function toggledModuleAccess(
  preferences: WorkerAccessPreferences | null | undefined,
  slug: string,
  enabled: boolean,
  allSlugs: readonly string[]
): string[] {
  const current = workerModuleAccess(preferences, allSlugs);
  if (enabled) {
    return current.includes(slug) ? current : [...current, slug];
  }
  return current.filter((s) => s !== slug);
}

/** W jakim stanie jest para (pracownik, moduł) — patrz `WorkerModuleState`. */
export function workerModuleState(hasAccess: boolean, isMeasured: boolean): WorkerModuleState {
  if (hasAccess && isMeasured) return 'measured';
  if (hasAccess) return 'assigned';
  if (isMeasured) return 'orphan';
  return 'unassigned';
}

// ─── Nazwy i filtrowanie listy ──────────────────────────────────────────────

export interface WorkerNameLike {
  first_name?: string | null;
  last_name?: string | null;
}

/** `Imię Nazwisko`, odporne na puste połówki (nie zostawia wiszącej spacji). */
export function workerFullName(worker: WorkerNameLike | null | undefined): string {
  if (!worker) return '';
  return [worker.first_name, worker.last_name]
    .map((part) => (part ?? '').trim())
    .filter(Boolean)
    .join(' ');
}

/** Inicjały do awatara — maksymalnie dwie litery, wielkimi. */
export function workerInitials(worker: WorkerNameLike | null | undefined): string {
  if (!worker) return '?';
  const first = (worker.first_name ?? '').trim().charAt(0);
  const last = (worker.last_name ?? '').trim().charAt(0);
  return (first + last).toUpperCase() || '?';
}

export interface WorkerSearchLike extends WorkerNameLike {
  email?: string | null;
  phone?: string | null;
  position?: string | null;
}

/**
 * Wspólne wyszukiwanie po liście pracowników. Diakrytyki są normalizowane, bo
 * „Maka" ma znajdować „Mąka" — kadrowa nie przełącza układu klawiatury, żeby
 * znaleźć swojego człowieka.
 */
export function matchesWorkerSearch(worker: WorkerSearchLike, term: string): boolean {
  const q = normalizeForSearch(term);
  if (!q) return true;
  const haystack = normalizeForSearch(
    [workerFullName(worker), worker.email, worker.position, worker.phone].filter(Boolean).join(' ')
  );
  return haystack.includes(q);
}

/**
 * Polska odmiana rzeczownika „osoba" po liczebniku — `1 osobę`, `2–4 osoby`,
 * `5+ osób`, z wyjątkiem 12–14, które idą jak 5+.
 *
 * Mieszka tutaj, bo pas „mierzeni, ale nieprzypisani" jest w SZEŚCIU organach
 * i każdy z nich pisze to samo zdanie. Trzy zakładki miały już własną poprawną
 * kopię tej funkcji, a trzy — skrót `n === 1 ? 'osobę' : 'osoby'`, który przy
 * pięciu sierotach wypisuje „mierzy 5 osoby".
 */
export function odmianaOsob(n: number): string {
  const liczba = Math.abs(Math.round(n));
  if (liczba === 1) return 'osobę';
  const reszta10 = liczba % 10;
  const reszta100 = liczba % 100;
  if (reszta10 >= 2 && reszta10 <= 4 && !(reszta100 >= 12 && reszta100 <= 14)) return 'osoby';
  return 'osób';
}

function normalizeForSearch(value: string | null | undefined): string {
  return (
    (value ?? '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      // „ł" nie rozkłada się na literę + znak diakrytyczny w NFD, więc wypada
      // z powyższego czyszczenia i wymaga własnej reguły.
      .replace(/ł/g, 'l')
      .trim()
  );
}

// ─── Katalog metryk ─────────────────────────────────────────────────────────
//
// Klucz metryki jest kontraktem między SQL-em (migracja 442) a UI. SQL zwraca
// gołe `metric_key` + `metric_value` + `metric_sample`; etykieta, jednostka,
// progi wydźwięku i to, która metryka jest „twarzą" modułu, mieszkają TUTAJ —
// żeby zmiana progu nie wymagała migracji, a etykieta nie była tłumaczona
// osobno w powłoce i w każdym module.

export interface WorkerMetricDef {
  key: string;
  module: string;
  label: string;
  format: WorkerMetricFormat;
  /** Metryka, którą moduł się przedstawia w kartotece. Jedna na moduł. */
  headline?: boolean;
  /**
   * Progi wydźwięku. `good`/`warn` to granice „od"; `invert: true` odwraca je
   * dla metryk, gdzie mniej znaczy lepiej (odchyłka temperatury).
   */
  tone?: { good: number; warn: number; invert?: boolean };
  hint?: string;
}

/**
 * Kolejność w tablicy = kolejność wyświetlania w karcie modułu.
 *
 * UWAGA na metryki bez `tone`: liczniki (ile pomiarów, ile zmian) NIE mają
 * wydźwięku, bo „dużo" nie znaczy „dobrze". Malowanie licznika na zielono
 * zamienia kartotekę w ranking wydajności człowieka, a to jest dokładnie ta
 * granica, której ten kontrakt nie przekracza.
 */
export const WORKER_METRIC_CATALOGUE: WorkerMetricDef[] = [
  // ── Temperatio (Water & Ice) ──
  {
    key: 'wai.accuracy',
    module: 'waterandice',
    label: 'Dokładność',
    format: 'percent',
    headline: true,
    tone: { good: 90, warn: 70 },
    hint: 'Udział pomiarów mieszczących się w ±1 °C od temperatury docelowej. Liczony wyłącznie z pomiarów, które mają OBIE temperatury — niekompletny wiersz nie jest już liczony jako trafiony.',
  },
  {
    key: 'wai.measurements',
    module: 'waterandice',
    label: 'Pomiary',
    format: 'count',
    hint: 'Liczba pomiarów temperatury ciasta w oknie.',
  },
  {
    key: 'wai.avg_deviation',
    module: 'waterandice',
    label: 'Śr. odchyłka',
    format: 'celsius',
    tone: { good: 1, warn: 3, invert: true },
    hint: 'Średnia wartość bezwzględna różnicy między temperaturą zmierzoną a docelową.',
  },
  {
    key: 'wai.completeness',
    module: 'waterandice',
    label: 'Kompletność danych',
    format: 'percent',
    tone: { good: 90, warn: 60 },
    hint: 'Udział pomiarów z uzupełnionymi obiema temperaturami. Niska wartość znaczy, że dokładność powyżej liczy się z małej próbki.',
  },
  {
    key: 'wai.batches',
    module: 'waterandice',
    label: 'Partie',
    format: 'count',
    hint: 'Partie ciasta prowadzone przez pracownika w oknie.',
  },
  {
    key: 'wai.batch_quality',
    module: 'waterandice',
    label: 'Jakość partii',
    format: 'percent',
    tone: { good: 80, warn: 60 },
    hint: 'Średnia ocena jakości partii (waterandice_batches.quality_score).',
  },
  {
    key: 'wai.haccp',
    module: 'waterandice',
    label: 'Zapisy HACCP',
    format: 'count',
    hint: 'Zapisy krytycznych punktów kontroli wykonane przez pracownika.',
  },

  // ── LexShift ──
  {
    key: 'lex.hours_planned',
    module: 'lexshift',
    label: 'Godziny w grafiku',
    format: 'hours',
    headline: true,
    hint: 'Suma godzin z wpisów grafiku (po odjęciu przerw) w oknie.',
  },
  {
    key: 'lex.shifts_planned',
    module: 'lexshift',
    label: 'Zmiany',
    format: 'count',
    hint: 'Liczba zaplanowanych zmian w oknie.',
  },
  {
    key: 'lex.attendance_days',
    module: 'lexshift',
    label: 'Dni z odbiciem',
    format: 'count',
    hint: 'Dni, w których pracownik zarejestrował wejście lub wyjście (kiosk / rozpoznawanie twarzy).',
  },
  {
    key: 'lex.hours_worked',
    module: 'lexshift',
    label: 'Godziny przepracowane',
    format: 'hours',
    hint: 'Suma z rozliczonych sesji pracy. Sesje powstają wyłącznie przy odbiciu na kiosku, więc dla części załogi próbka bywa pusta.',
  },
  {
    key: 'lex.overtime_h',
    module: 'lexshift',
    label: 'Nadgodziny',
    format: 'hours',
    tone: { good: 4, warn: 12, invert: true },
    hint: 'Nadgodziny z rozliczonych sesji pracy w oknie.',
  },
  {
    key: 'lex.leave_days',
    module: 'lexshift',
    label: 'Dni urlopu',
    format: 'days',
    hint: 'Zatwierdzone dni nieobecności, których zakres wpada w okno.',
  },

  // ── Aegis (gamification) ──
  {
    key: 'aegis.points_window',
    module: 'gamification',
    label: 'Punkty w oknie',
    format: 'points',
    headline: true,
    hint: 'Punkty zdobyte w oknie, z dziennika aktywności.',
  },
  {
    key: 'aegis.total_points',
    module: 'gamification',
    label: 'Punkty łącznie',
    format: 'points',
    hint: 'Dorobek od początku.',
  },
  {
    key: 'aegis.level',
    module: 'gamification',
    label: 'Poziom',
    format: 'level',
    hint: 'Bieżący poziom pracownika.',
  },
  {
    key: 'aegis.achievements',
    module: 'gamification',
    label: 'Odznaki',
    format: 'count',
    hint: 'Zdobyte osiągnięcia (łącznie).',
  },
  {
    key: 'aegis.skills',
    module: 'gamification',
    label: 'Umiejętności',
    format: 'count',
    hint: 'Umiejętności prowadzone w ścieżce rozwoju.',
  },
  {
    key: 'aegis.activities',
    module: 'gamification',
    label: 'Zdarzenia',
    format: 'count',
    hint: 'Wpisy w dzienniku aktywności w oknie.',
  },

  // ── Agenda ──
  {
    key: 'agenda.on_time',
    module: 'agenda',
    label: 'Terminowość',
    format: 'percent',
    headline: true,
    tone: { good: 90, warn: 70 },
    hint: 'Udział zadań ukończonych w terminie wśród ukończonych w oknie.',
  },
  {
    key: 'agenda.assigned',
    module: 'agenda',
    label: 'Zadania przydzielone',
    format: 'count',
    hint: 'Zadania przypisane pracownikowi w oknie.',
  },
  {
    key: 'agenda.completed',
    module: 'agenda',
    label: 'Zadania ukończone',
    format: 'count',
    hint: 'Zadania zamknięte przez pracownika w oknie.',
  },
  {
    key: 'agenda.hours_logged',
    module: 'agenda',
    label: 'Czas zaraportowany',
    format: 'hours',
    hint: 'Suma czasu z dziennika pracy nad zadaniami.',
  },

  // ── Fabrica ──
  {
    key: 'fabrica.first_pass',
    module: 'fabrica',
    label: 'Za pierwszym razem',
    format: 'percent',
    headline: true,
    tone: { good: 90, warn: 75 },
    hint: 'Udział prób zakończonych powodzeniem za pierwszym podejściem.',
  },
  {
    key: 'fabrica.stages',
    module: 'fabrica',
    label: 'Etapy produkcji',
    format: 'count',
    hint: 'Etapy zlecenia zamknięte przez pracownika w oknie.',
  },
  {
    key: 'fabrica.minutes',
    module: 'fabrica',
    label: 'Czas przy linii',
    format: 'minutes',
    hint: 'Suma czasu trwania etapów prowadzonych przez pracownika.',
  },

  // ── Conspectus (organ wzroku) ──
  {
    key: 'conspectus.wskazania',
    module: 'conspectus',
    label: 'Wskazania',
    format: 'count',
    headline: true,
    hint: 'Ile razy pracownik został wskazany na materiale z kamer.',
  },
  {
    key: 'conspectus.incidents',
    module: 'conspectus',
    label: 'Zdarzenia',
    format: 'count',
    hint: 'Zdarzenia powiązane z pracownikiem w oknie.',
  },
];

const METRIC_BY_KEY = new Map(WORKER_METRIC_CATALOGUE.map((def) => [def.key, def]));

/** Definicja metryki albo `undefined`, gdy SQL zwrócił klucz spoza katalogu. */
export function workerMetricDef(key: string): WorkerMetricDef | undefined {
  return METRIC_BY_KEY.get(key);
}

/** Wszystkie moduły, dla których katalog cokolwiek liczy — w kolejności katalogu. */
export const WORKER_MEASURED_MODULES: string[] = WORKER_METRIC_CATALOGUE.reduce<string[]>(
  (acc, def) => (acc.includes(def.module) ? acc : [...acc, def.module]),
  []
);

// ─── Wydźwięk i formatowanie ────────────────────────────────────────────────

/** Semantyczny wydźwięk metryki. Bez progów w katalogu — zawsze `neutral`. */
export function metricTone(def: WorkerMetricDef, value: number | null): WorkerMetricTone {
  if (value === null || !def.tone) return 'neutral';
  const { good, warn, invert } = def.tone;
  if (invert) {
    if (value <= good) return 'good';
    if (value <= warn) return 'warn';
    return 'bad';
  }
  if (value >= good) return 'good';
  if (value >= warn) return 'warn';
  return 'bad';
}

/**
 * Tekst do wyświetlenia. Brak danych to `—`, NIGDY `0`. Separator dziesiętny
 * jest polski, bo cały interfejs jest po polsku.
 */
export function formatWorkerMetric(metric: WorkerMetric): string {
  if (metric.value === null) return '—';
  const v = metric.value;
  switch (metric.format) {
    case 'percent':
      return `${Math.round(v)}%`;
    case 'celsius':
      return `±${decimal(v, 1)} °C`;
    case 'hours':
      return `${decimal(v, 1)} h`;
    case 'minutes':
      return v >= 60 ? `${decimal(v / 60, 1)} h` : `${Math.round(v)} min`;
    case 'days':
      return `${decimal(v, 1)} dni`;
    case 'points':
      return `${Math.round(v)} pkt`;
    case 'level':
      return `Poziom ${Math.round(v)}`;
    case 'count':
    default:
      return `${Math.round(v)}`;
  }
}

function decimal(value: number, places: number): string {
  return value.toFixed(places).replace('.', ',');
}

/**
 * Podpis o wielkości próbki — pokazywany, gdy liczba stoi na cienkich danych.
 * `null` znaczy „nie ma czego dopisywać".
 */
export function metricSampleNote(metric: WorkerMetric, thinBelow = 5): string | null {
  if (metric.sample === 0) return 'brak danych';
  if (metric.sample < thinBelow) return `mała próbka (${metric.sample})`;
  return null;
}

// ─── Składanie wyniku RPC ───────────────────────────────────────────────────

export interface FoldStatRowsOptions {
  /** Wszystkie moduły, które kartoteka bierze pod uwagę (kolejność zachowana). */
  allSlugs: readonly string[];
  /** Efektywne przypisania pracownika — wynik `workerModuleAccess`. */
  accessSlugs: readonly string[];
  windowDays: number;
  /** Trasa powłoki do ekranu pracowników danego modułu, jeśli istnieje. */
  deepLink?: (slug: string) => string | null;
}

/**
 * Zwija długie wiersze z `universe_worker_module_stats` w karty wyników —
 * po jednej na moduł z `allSlugs`, także dla modułów bez ani jednego wiersza
 * (wtedy `is_measured === false`). Moduł bez danych MUSI zostać na liście:
 * „przypisany, ale nic tu nie robi" to informacja, a nie pusty rekord do
 * ukrycia.
 */
export function foldStatRows(
  rows: readonly WorkerModuleStatRow[],
  options: FoldStatRowsOptions
): WorkerModuleScorecard[] {
  const { allSlugs, accessSlugs, windowDays, deepLink } = options;

  const byModule = new Map<string, WorkerModuleStatRow[]>();
  let workerId = '';
  for (const row of rows) {
    workerId = workerId || row.worker_id;
    const bucket = byModule.get(row.module_slug);
    if (bucket) bucket.push(row);
    else byModule.set(row.module_slug, [row]);
  }

  return allSlugs.map((slug) => {
    const moduleRows = byModule.get(slug) ?? [];

    const metrics: WorkerMetric[] = WORKER_METRIC_CATALOGUE.filter((def) => def.module === slug)
      .map((def) => {
        const row = moduleRows.find((r) => r.metric_key === def.key);
        const sample = row?.metric_sample ?? 0;
        // Wartość liczy się tylko wtedy, gdy próbka nie jest pusta. Bez tego
        // `avg()` po zerowym zbiorze i „naprawdę zero" wyglądałyby identycznie.
        const value = sample > 0 && row?.metric_value != null ? Number(row.metric_value) : null;
        return {
          key: def.key,
          label: def.label,
          value,
          format: def.format,
          sample,
          tone: metricTone(def, value),
          hint: def.hint,
        } satisfies WorkerMetric;
      })
      .filter((metric) => metric.sample > 0 || metric.value !== null);

    const isMeasured = metrics.some((m) => m.sample > 0);

    // Twarz modułu: metryka oznaczona `headline`, a gdy ta nie ma próbki —
    // pierwsza, która ją ma. Karta z „—" na środku nic nie mówi, a moduł
    // prawie zawsze ma czym się przedstawić, tylko nie zawsze tym samym.
    const headlineDef = WORKER_METRIC_CATALOGUE.find((d) => d.module === slug && d.headline);
    const headline =
      metrics.find((m) => m.key === headlineDef?.key && m.sample > 0) ??
      metrics.find((m) => m.sample > 0) ??
      null;

    const lastActivity = moduleRows.reduce<string | null>((acc, row) => {
      if (!row.last_activity_at) return acc;
      return !acc || row.last_activity_at > acc ? row.last_activity_at : acc;
    }, null);

    return {
      module: slug,
      worker_id: workerId,
      has_access: accessSlugs.includes(slug),
      is_measured: isMeasured,
      window_days: windowDays,
      headline,
      metrics,
      last_activity_at: lastActivity,
      deep_link: deepLink ? deepLink(slug) : null,
    } satisfies WorkerModuleScorecard;
  });
}

/** Grupuje płaską odpowiedź RPC po pracowniku — kartoteka pobiera całą załogę naraz. */
export function groupStatRowsByWorker(
  rows: readonly WorkerModuleStatRow[]
): Map<string, WorkerModuleStatRow[]> {
  const out = new Map<string, WorkerModuleStatRow[]>();
  for (const row of rows) {
    const bucket = out.get(row.worker_id);
    if (bucket) bucket.push(row);
    else out.set(row.worker_id, [row]);
  }
  return out;
}

/** Ile modułów faktycznie mierzy tego pracownika — liczba do nagłówka karty. */
export function measuredModuleCount(scorecards: readonly WorkerModuleScorecard[]): number {
  return scorecards.filter((s) => s.is_measured).length;
}

/**
 * Moduły, w których pracownik jest mierzony MIMO braku przypisania. To sygnał
 * niespójności danych, nie ozdoba: ktoś pracuje w organie, do którego nikt go
 * nie zapisał, więc na liście tego organu go nie widać.
 */
export function orphanModules(scorecards: readonly WorkerModuleScorecard[]): string[] {
  return scorecards.filter((s) => s.is_measured && !s.has_access).map((s) => s.module);
}

// ─── Soczewka organu ────────────────────────────────────────────────────────
//
// Zakładka Pracownicy w module pokazuje ludzi W ŚWIETLE TEGO ORGANU. Wzór jest
// za każdym razem ten sam — pobierz załogę, policz karty wyników dla swojego
// sluga, podziel listę — i był już trzykrotnie przepisany ręcznie. Poniższe
// funkcje są tym wzorem wyjętym do jednego miejsca: czyste, bez Reacta,
// testowane raz dla wszystkich sześciu organów.

/**
 * Karty wyników JEDNEGO organu, po jednej na pracownika, złożone z płaskiej
 * odpowiedzi `universe_worker_module_stats` (wywołanej z `p_module_slug`).
 *
 * Każda zakładka robiła to dotąd u siebie: mapa `worker_id → wiersze`, potem
 * `foldStatRows` z jednoelementowym `allSlugs`. Cztery linijki, ale cztery
 * linijki, w których łatwo pomylić `accessSlugs` (tutaj ZAWSZE równe
 * `[slug]` — karta liczy wyniki organu, a nie odpowiada na pytanie
 * o przypisanie; od tego jest `workerModuleLens`).
 */
export function moduleScorecardsByWorker(
  rows: readonly WorkerModuleStatRow[],
  options: { slug: string; windowDays: number }
): Map<string, WorkerModuleScorecard> {
  const { slug, windowDays } = options;
  const out = new Map<string, WorkerModuleScorecard>();
  // `forEach`, a nie `for...of`: ten pakiet kompiluje się także w modułach,
  // których `tsconfig` celuje niżej niż ES2015, a iteracja po Mapie wymaga tam
  // `--downlevelIteration`. Zmiana na pętlę wywraca build tamtych aplikacji.
  groupStatRowsByWorker(rows).forEach((workerRows, workerId) => {
    const [card] = foldStatRows(workerRows, {
      allSlugs: [slug],
      accessSlugs: [slug],
      windowDays,
    });
    if (card) out.set(workerId, card);
  });
  return out;
}

/** Pracownicy, dla których organ ma w oknie jakiekolwiek dane — czyli MIERZENI. */
export function measuredWorkerIds(
  scorecards: ReadonlyMap<string, WorkerModuleScorecard>
): Set<string> {
  const out = new Set<string>();
  scorecards.forEach((card, workerId) => {
    if (card.is_measured) out.add(workerId);
  });
  return out;
}

/** Minimum, jakiego potrzebuje podział załogi na soczewkę organu. */
export interface WorkerLensLike extends WorkerAccessLike, WorkerSearchLike {
  id: string;
  is_active?: boolean | null;
}

export interface WorkerLensOptions {
  /** Slug organu, którego to jest zakładka. */
  slug: string;
  /** Pełny skład organów — bez niego nie da się policzyć permisywnego defaultu. */
  allSlugs: readonly string[];
  /** Kto jest w tym organie mierzony (wynik `measuredWorkerIds`). */
  measured?: ReadonlySet<string>;
  /** Fraza z pola wyszukiwania. Dotyczy `crew` i `orphans`, nigdy `candidates`. */
  search?: string;
  /** Czy pokazywać nieaktywnych. Domyślnie nie — organ pracuje z aktywną załogą. */
  showInactive?: boolean;
  /**
   * Czy przynależność musi być wpisana WPROST (bez permisywnego defaultu).
   * Domyślnie `false`, więc dla każdego organu, który tego nie zażądał, nic się
   * nie zmienia. Patrz „ZASTRZEŻENIE POJEDYNCZEGO ORGANU" na górze pliku.
   */
  requireExplicit?: boolean;
}

export interface WorkerLens<T> {
  /**
   * Załoga organu: przypisani, po filtrze aktywności i wyszukiwania. To jest
   * lista, którą zakładka pokazuje jako swoją.
   */
  crew: T[];
  /**
   * Przypisani BEZ ŻADNYCH filtrów — łącznie z nieaktywnymi i odsianymi przez
   * wyszukiwanie. Do kafelków podsumowania: liczba opisująca organ nie może
   * zmieniać się przy pisaniu w polu wyszukiwania, bo wtedy przestaje opisywać
   * organ, a zaczyna opisywać frazę.
   */
  assigned: T[];
  /**
   * MIERZENI W TYM ORGANIE, ALE NIEPRZYPISANI. Nie jest to lista poboczna,
   * tylko usterka danych pokazana wprost: organ ma wyniki tych ludzi, a na
   * własnej liście ich nie widzi — bo nikt ich do niego nie zapisał. Ukrycie
   * ich (co robił pierwszy kształt tej soczewki) sprawia, że kartoteka Universe
   * pokazuje wyniki, których w module nie da się znaleźć. Na produkcji dotyczy
   * to dziś dwóch z trzech osób z zadaniami w Agendzie.
   */
  orphans: T[];
  /** Kandydaci do szuflady „Przypisz": aktywni spoza organu. Bez filtrów listy. */
  candidates: T[];
  /** Ilu pracowników jest do organu przypisanych (łącznie z nieaktywnymi). */
  assignedTotal: number;
  /** Ilu z przypisanych jest nieaktywnych — liczba opisująca skład organu. */
  inactiveAssigned: number;
  /**
   * Ilu ludzi ekran UKRYWA wyłącznie dlatego, że są nieaktywni — przypisanych
   * ORAZ sierot. To jest liczba dla przełącznika „Pokaż nieaktywnych", a nie
   * `inactiveAssigned`.
   *
   * Różnica nie jest kosmetyczna. Zakładki wyłączają przełącznik, gdy licznik
   * jest zerowy; przy `inactiveAssigned` nieaktywna SIEROTA (mierzona, nigdy
   * nieprzypisana, dziś zdezaktywowana) była na ekranie nieosiągalna: nie
   * wchodziła do `crew`, bo nie jest przypisana, wypadała z `orphans` przez
   * filtr aktywności, a przełącznik, który by ją pokazał, był wygaszony, bo
   * przypisanych nieaktywnych nie było ani jednego.
   *
   * Zależy od `showInactive`: gdy nieaktywni są już pokazani, nic nie jest
   * ukryte i liczba spada do zera.
   */
  inactiveHidden: number;
  /**
   * Ilu AKTYWNYCH pracowników nie ma w ogóle ustawionych modułów.
   *
   * Przy `requireExplicit` to jest dokładnie liczba ludzi, których zakładka
   * trzyma poza załogą nie dlatego, że ktoś ich stąd wypisał, tylko dlatego, że
   * nikt ich tu nie zapisał — i tę różnicę ekran ma nazwać. Przy permisywnym
   * defaultcie ta sama liczba mówi, ilu ludzi jest na liście „z braku decyzji",
   * a nie z decyzji.
   */
  undeclared: number;
}

/**
 * Dzieli załogę firmy na cztery kategorie widziane oczami jednego organu.
 *
 * Filtruje TABLICĘ, nigdy zapytanie — i to jest wymóg, nie preferencja.
 * Odsianie kogoś w SQL-u sprawia, że jego wiersze historyczne przestają mieć
 * do kogo należeć (nazwiska rozwiązują się po stronie klienta z tej samej
 * listy), więc jedna dezaktywacja zamienia historię w „Nieznany pracownik" —
 * dokładnie to wydarzyło się 4.08.2026 w LexShifcie.
 */
export function workerModuleLens<T extends WorkerLensLike>(
  workers: readonly T[],
  options: WorkerLensOptions
): WorkerLens<T> {
  const {
    slug,
    allSlugs,
    measured,
    search = '',
    showInactive = false,
    requireExplicit = false,
  } = options;

  const widoczny = (w: T) =>
    (showInactive || w.is_active !== false) && matchesWorkerSearch(w, search);

  const nalezy = (w: T) =>
    requireExplicit
      ? hasExplicitModuleAccess(w.preferences, slug)
      : hasModuleAccess(w.preferences, slug, allSlugs);

  const crew: T[] = [];
  const assigned: T[] = [];
  const orphans: T[] = [];
  const candidates: T[] = [];
  let inactiveAssigned = 0;
  let inactiveHidden = 0;
  let undeclared = 0;

  for (const w of workers) {
    const nieaktywny = w.is_active === false;
    // Ukryty WYŁĄCZNIE z powodu nieaktywności — czyli taki, którego pokazanie
    // zależy od samego przełącznika, a nie od frazy w wyszukiwarce.
    const ukrytyPrzezAktywnosc = nieaktywny && !showInactive && matchesWorkerSearch(w, search);

    // Luka w danych liczona po AKTYWNYCH: kogoś zdezaktywowanego nikt już do
    // organu nie dopisze, więc jego brak deklaracji nie jest zadaniem do zrobienia.
    if (!nieaktywny && !hasModuleDeclaration(w.preferences)) undeclared += 1;

    if (nalezy(w)) {
      assigned.push(w);
      if (nieaktywny) inactiveAssigned += 1;
      if (ukrytyPrzezAktywnosc) inactiveHidden += 1;
      if (widoczny(w)) crew.push(w);
      continue;
    }
    if (!nieaktywny) candidates.push(w);
    if (measured?.has(w.id)) {
      if (widoczny(w)) orphans.push(w);
      else if (ukrytyPrzezAktywnosc) inactiveHidden += 1;
    }
  }

  return {
    crew,
    assigned,
    orphans,
    candidates,
    assignedTotal: assigned.length,
    inactiveAssigned,
    inactiveHidden,
    undeclared,
  };
}
